import { PortalCommunicationOperator } from '@app/shared/utils/communication-operator.util';
import {
  ConsumerType,
  OfferingProductCategory,
  OfferingProductCategoryName,
  ServiceOffering,
} from '@shared/types/service-offering';
import {
  portalConfig,
  ServiceProvider,
  ServiceSortingOption,
  StaticIspUrlKeys,
  SubscriptionType,
} from '../entities';
import { servicesFilterAndSort } from './service-filters.util';

/* Main filter */
export function filterServiceOfferings(
  services: ServiceOffering[],
  selectedSortingFilter: ServiceSortingOption,
  selectedIsp: ServiceProvider,
  onlyOrderable: boolean,
  companyOffers: boolean,
  currentServices: SubscriptionType[]
): ServiceOffering[] {
  const roughFilteredUnsorted = services.filter(
    (service) =>
      filterIsp(service, selectedIsp) &&
      filterConsumerType(service, companyOffers) &&
      filterCategory(service, currentServices, onlyOrderable)
  );
  return servicesFilterAndSort(roughFilteredUnsorted, selectedSortingFilter);
}

function filterIsp(
  service: ServiceOffering,
  selectedIsp: ServiceProvider
): boolean {
  return selectedIsp?.id === StaticIspUrlKeys.ALL ||
    selectedIsp?.id === StaticIspUrlKeys.SHUFFLED
    ? true
    : service.serviceProviderName === selectedIsp.name;
}

function filterCategory(
  service: ServiceOffering,
  currentServices: SubscriptionType[],
  orderable: boolean
): boolean {
  const convertedOfferCategories = offerToSubscriptionConverter(
    service.productCategories
  );

  return orderable
    ? !isProductCategoryActive(convertedOfferCategories, currentServices)
    : true;
}

function isProductCategoryActive(
  currentServices: SubscriptionType[],
  productCategories: SubscriptionType[]
) {
  return productCategories.some((category) =>
    currentServices.includes(category)
  );
}

function offerToSubscriptionConverter(
  categories: OfferingProductCategory[]
): SubscriptionType[] {
  const services: SubscriptionType[] = [];

  if (categories === undefined) {
    return services;
  }

  categories.forEach((category) => {
    if (category.name === OfferingProductCategoryName.Internet) {
      services.push(SubscriptionType.Internet);
    } else if (category.name === OfferingProductCategoryName.Phone) {
      services.push(SubscriptionType.Phone);
    } else if (category.name === OfferingProductCategoryName.Tv) {
      services.push(SubscriptionType.Tv);
    }
  });
  return services;
}

function filterConsumerType(
  service: ServiceOffering,
  companyOffer: boolean
): boolean {
  return companyOffer
    ? service.consumerType === ConsumerType.Corporate
    : service.consumerType === ConsumerType.Private;
}

/* Bundle filters */
export function filterInternetPhoneBundles(
  services: ServiceOffering[]
): ServiceOffering[] {
  return services.filter((service) => {
    return (
      service.productCategories.find(
        (c) => c.name === OfferingProductCategoryName.Internet
      ) &&
      service.productCategories.find(
        (c) => c.name === OfferingProductCategoryName.Phone
      )
    );
  });
}

export function filterInternetTvBundles(
  services: ServiceOffering[]
): ServiceOffering[] {
  return services.filter((service) => {
    return (
      service.productCategories.find(
        (c) => c.name === OfferingProductCategoryName.Internet
      ) &&
      service.productCategories.find(
        (c) => c.name === OfferingProductCategoryName.Tv
      )
    );
  });
}

/* Specific category filters (exclusive filtering) */
export function filterInternet(services: ServiceOffering[]): ServiceOffering[] {
  return services.filter((service) => isInternetService(service));
}

export function filterTvServices(
  services: ServiceOffering[]
): ServiceOffering[] {
  return services.filter(
    (service) =>
      service.productCategories.length === 1 &&
      service.productCategories[0].name === OfferingProductCategoryName.Tv
  );
}

export function filterPhoneServices(
  services: ServiceOffering[]
): ServiceOffering[] {
  return services.filter(
    (service) =>
      service.productCategories.length === 1 &&
      service.productCategories[0].name === OfferingProductCategoryName.Phone
  );
}

export function filterOtherServices(
  services: ServiceOffering[]
): ServiceOffering[] {
  return services.filter(
    (service) =>
      service.productCategories.length === 1 &&
      service.productCategories[0].name === OfferingProductCategoryName.Other
  );
}

/* Speed filters */
export function filterSpeed5000(
  services: ServiceOffering[]
): ServiceOffering[] {
  return services.filter(
    (service) =>
      isInternetService(service) &&
      2500 < service.speedDown &&
      service.speedDown <= 5000
  );
}

export function filterSpeed2500(
  services: ServiceOffering[]
): ServiceOffering[] {
  return services.filter(
    (service) =>
      isInternetService(service) &&
      1000 < service.speedDown &&
      service.speedDown <= 2500
  );
}

export function filterSpeed1000(
  services: ServiceOffering[]
): ServiceOffering[] {
  return services.filter(
    (service) =>
      isInternetService(service) &&
      500 < service.speedDown &&
      service.speedDown <= 1000
  );
}

export function filterSpeed500(services: ServiceOffering[]): ServiceOffering[] {
  return services.filter(
    (service) =>
      isInternetService(service) &&
      250 < service.speedDown &&
      service.speedDown <= 500
  );
}

export function filterSpeed250(services: ServiceOffering[]): ServiceOffering[] {
  return services.filter(
    (service) =>
      isInternetService(service) &&
      100 < service.speedDown &&
      service.speedDown <= 250
  );
}

export function filterSpeedUpTo250(
  services: ServiceOffering[]
): ServiceOffering[] {
  return services.filter(
    (service) =>
      isInternetService(service) &&
      0 < service.speedDown &&
      service.speedDown <= 250
  );
}

export function filterSpeedInterval30To100(
  services: ServiceOffering[]
): ServiceOffering[] {
  return services.filter(
    (service) =>
      isInternetService(service) &&
      30 < service.speedDown &&
      service.speedDown <= 100
  );
}

export function filterSpeedSlow(
  services: ServiceOffering[]
): ServiceOffering[] {
  return services.filter(
    (service) => isInternetService(service) && service.speedDown <= 10
  );
}

export function isInternetService(service: ServiceOffering): boolean {
  return (
    service.productCategories.length === 1 &&
    service.productCategories[0].name === OfferingProductCategoryName.Internet
  );
}

export function filterSelectedOffers(
  services: ServiceOffering[]
): ServiceOffering[] {
  // filters so that each element is unique based on serviceProviderName
  return services.filter(
    (item, index, self) =>
      self.findIndex(
        (el) => el.serviceProviderName === item.serviceProviderName
      ) === index
  );
}

export function filterUniqueOffers(
  services: ServiceOffering[]
): ServiceOffering[] {
  return services.filter((service) => service.isUniqueOffer);
}

/** Sorting */

export function shuffle(array: ServiceOffering[]): ServiceOffering[] {
  const newArray = [...array]; // Create a copy of the original array to avoid modifying it.

  for (let i = newArray.length - 1; i > 0; i--) {
    // Generate a random index between 0 and i (inclusive).
    const randomIndex = Math.floor(Math.random() * (i + 1));

    // Swap elements at randomIndex and i.
    [newArray[i], newArray[randomIndex]] = [newArray[randomIndex], newArray[i]];
  }

  return newArray;
}

export function sortAlphabeticallyThenSpeedThenPrice(
  a: ServiceOffering,
  b: ServiceOffering
): number {
  return (
    a.serviceProviderName.localeCompare(b.serviceProviderName) ||
    sortBySpeedThenPrice(a, b)
  );
}

export function sortBySpeedThenPrice(
  a: ServiceOffering,
  b: ServiceOffering
): number {
  return sortBySpeedDownDescending(a, b) || sortByPrice(a, b);
}

export function sortByPrice(a: ServiceOffering, b: ServiceOffering): number {
  return (
    sortByCampaignPriceAscending(a, b) ||
    sortByRegularPriceAscending(a, b) ||
    sortByStartingFeeAscending(a, b)
  );
}

function sortBySpeedDownDescending(
  a: ServiceOffering,
  b: ServiceOffering
): number {
  return b.speedDown - a.speedDown;
}

function sortByCampaignPriceAscending(
  a: ServiceOffering,
  b: ServiceOffering
): number {
  const aFee =
    a.isCampaign && a.campaignRecurringMonthlyFee
      ? a.campaignRecurringMonthlyFee
      : Number.POSITIVE_INFINITY;
  const bFee =
    b.isCampaign && b.campaignRecurringMonthlyFee
      ? b.campaignRecurringMonthlyFee
      : Number.POSITIVE_INFINITY;
  return aFee - bFee;
}

function sortByRegularPriceAscending(
  a: ServiceOffering,
  b: ServiceOffering
): number {
  return a.recurringMonthlyFee - b.recurringMonthlyFee;
}

function sortByStartingFeeAscending(
  a: ServiceOffering,
  b: ServiceOffering
): number {
  return a.startFee - b.startFee;
}

/* Finnish priority of sorting */
export function sortBySpeedThenEffectivePriceThenRandomize(
  a: ServiceOffering,
  b: ServiceOffering
): number {
  const speedCompare = sortBySpeedDownDescending(a, b);
  if (speedCompare !== 0) {
    return speedCompare;
  }

  const priceCompare = sortByPrice(a, b);
  if (priceCompare !== 0) {
    return priceCompare;
  }

  return Math.random() < 0.5 ? -1 : 1;
}

export function getDefaultSortingOption(
  portal: PortalCommunicationOperator,
  showUniqueOffers: boolean,
  isOnGeneralPage: boolean
): ServiceSortingOption {
  const sortingConfig = portalConfig[portal];

  if (sortingConfig.defaultSorting === 'SELECTED_OFFERS_PLACEHOLDER') {
    if (showUniqueOffers && !isOnGeneralPage) {
      return 'UNIQUE_OFFERS';
    }
  }
  return sortingConfig.defaultSorting;
}

export function getDefaultIspFilter(
  portal: PortalCommunicationOperator,
  translate: (key: string) => string
) {
  const filterConfig = portalConfig[portal];

  const labelKey =
    filterConfig.defaultIsp === StaticIspUrlKeys.ALL
      ? 'filters.isp.all'
      : 'filters.isp.placeholder';

  return {
    id: filterConfig.defaultIsp,
    name: translate(labelKey),
  };
}

/* Wildcard for filtering out vacationHome service cards */
export function filterOutShortTermServices(
  offers: ServiceOffering[]
): ServiceOffering[] {
  const shortTermBaseServicePattern = /_Korttid med Bastjänst.*dag/i;

  return offers.filter(
    (offer) => !shortTermBaseServicePattern.test(offer.name)
  );
}
